import type {
    Grade,
    StudentAnswerReviewItem,
    StudentQuestionAnswer,
    StudentTestAnswer,
    StudentTestContent,
    TestDetailsWord,
    StudentTestExercise,
} from "p6m-p6u";
import { Exercises, ExerciseTypes } from "../enums/exercises";
import { getDisplayableTestLanguageName } from "./activityLanguageHelpers";
import { getExerciseTypeByQuestionMode } from "./ExerciseTypesHelper";
import { getExerciseScoringByMode, getScoreForExercise, setMissingScoresForExercise } from "./ScoringHelper";

/**
 * The shape the student result page renders, built once so every slot reads the same numbers.
 *
 * Only the evaluated-&-shared state reaches this page, and evaluation runs the result through
 * `processNewResultsArray` (see `TestReview.ts`) first — so `studentAnswers` is always populated
 * and already normalised (e.g. text tasks carry a synthetic single answer).
 */

export enum StudentResultAnswerStates {
    CORRECT = "CORRECT",
    PARTIALLY_CORRECT = "PARTIALLY_CORRECT",
    INCORRECT = "INCORRECT",
    NO_ANSWER = "NO_ANSWER",
}

export const getIsAnswerCorrect = (state: StudentResultAnswerStates): boolean =>
    state === StudentResultAnswerStates.CORRECT;

export interface StudentResultAnswer {
    /** Unique within its exercise — for rendering, not for matching back to a word. */
    id: string;
    question: string;
    gapSentence: string;
    /**
     * The word's other side, whichever the exercise did not ask for — the prompt on a reversed row.
     * Not the solution, and not a source for one: the student-test strips whatever was asked for.
     */
    otherSide: string;
    studentAnswer: string;
    state: StudentResultAnswerStates;
    reachedScore: number;
    maxScore: number;
    teacherComment: string;
    /** Connect-words only. */
    color: string;
    /** Set per word, so a single exercise mixes both directions. */
    isQuestionShown: boolean;
}

export interface StudentResultExercise {
    id: string;
    /** 1-based, as shown to the student ("Aufgabe 3"). */
    number: number;
    /** The questionMode resolved to the enum the renderers switch on. */
    exerciseKind?: Exercises;
    /** The teacher's `extraComments`. */
    description: string;
    /** In the shuffled order the student saw. */
    wordBox?: string[];
    /** The teacher's `textFieldStarterText`. */
    helperSentence?: string;
    answers: StudentResultAnswer[];
    reachedScore: number;
    maxScore: number;
    /** Repeated from the result so the answer renderers need no extra prop. */
    isScored: boolean;
}

export interface StudentResult {
    testName: string;
    /** Epoch ms of the student's submission; 0 when the result carries no `sentTime`. */
    submittedDate: number;
    /** The test's foreign language ("Englisch"); "" when nothing names one worth showing. */
    languageName: string;
    exercises: StudentResultExercise[];
    reachedScore: number;
    maxScore: number;
    overallComment: string;
    /**
     * The teacher turned points on for this test. False means every maximum is a flat 1, so the
     * point numbers say nothing the right/wrong marks do not — which is why the view hides them.
     */
    isScored: boolean;
}

const _sum = (values: number[]): number => values.reduce((total, value) => total + value, 0);

/**
 * The side the student was asked from. A reversed row carries no question at all — the share
 * transform keeps only the shown side — so the prompt is then the other side, never the question.
 */
export const getAnswerPrompt = (answer: StudentResultAnswer): string => {
    return answer.isQuestionShown ? answer.question : answer.otherSide;
};

/**
 * One decimal, as the design displays it ("68,5 %"); 0 when there is nothing to score against.
 * Clamped to 0–100 so bonus points cannot push it past the scale.
 */
export const getReachedPercentage = (reachedScore: number, maxScore: number): number => {
    if (maxScore <= 0) {
        return 0;
    }
    const rounded = Math.round((reachedScore / maxScore) * 1000) / 10;
    return Math.min(100, Math.max(0, rounded));
};

/** An unscored test's maximum for every markable item — see `docs/evaluation`. */
const UNSCORED_ITEM_SCORE = 1;

/**
 * The exercise's maxima, as the result should score them. Scored fills gaps as the test PDF does
 * (`ensureReliableOverallScore` → `handleMissingScores`); unscored *overwrites*, because
 * `fixPDFScore` writes scores into the content while `showScores` is off and those weights would
 * skew a percentage the student is meant to be able to reconstruct.
 *
 * An unrecognised `questionMode` keeps its 0: an exercise we cannot identify should not invent
 * points.
 */
const _getScoredExercise = (studentTestExercise: StudentTestExercise, isScored: boolean): StudentTestExercise => {
    const scoring = getExerciseScoringByMode(studentTestExercise.questionMode ?? "");

    if (isScored) {
        return setMissingScoresForExercise(studentTestExercise, scoring) as StudentTestExercise;
    }

    if (scoring.storesScorePerWord) {
        return {
            ...studentTestExercise,
            selectedWords: (studentTestExercise.selectedWords || []).map((word) => ({
                ...word,
                wordScore: UNSCORED_ITEM_SCORE,
            })),
        };
    }

    if (!getExerciseTypeByQuestionMode(studentTestExercise.questionMode ?? "")) {
        return studentTestExercise;
    }

    return { ...studentTestExercise, exerciseScore: UNSCORED_ITEM_SCORE };
};

/**
 * The only place the teacher's verdict becomes points — keep it that way. E2 reads a saved
 * per-answer score here instead of deriving it, and the long-term direction drops the state entirely
 * in favour of points (`docs/evaluation/evaluation-scoring-model.md`); a second conversion elsewhere
 * makes both a rewrite rather than a one-liner.
 *
 * A blank the teacher marked correct still scores: the number must match the teacher's own view. Its
 * *state* stays `NO_ANSWER` on purpose — the student did not answer, and the display says so.
 */
const _getReachedScore = (teacherJudgement: Grade | undefined, maxScore: number): number => {
    return teacherJudgement === "correct" ? maxScore : 0;
};

/**
 * Anything not `correct` reads as incorrect, `not-graded` included — safe because sharing requires
 * every answer graded (`TestReviewContext` only shares at 100% evaluated), so an ungraded answer
 * never reaches a shared result.
 */
const _getAnswerState = (teacherJudgement: Grade | undefined, studentAnswer: string): StudentResultAnswerStates => {
    // Checked first: a blank reads as unanswered whatever the teacher marked — but still scores (`_getReachedScore`).
    if (!studentAnswer.trim()) {
        return StudentResultAnswerStates.NO_ANSWER;
    }
    if (teacherJudgement === "correct") {
        return StudentResultAnswerStates.CORRECT;
    }
    return StudentResultAnswerStates.INCORRECT;
};

/** The student-test holds the points and the per-answer direction; the result rows hold neither. */
const _buildWordsById = (words: TestDetailsWord[]): Map<string, TestDetailsWord> => {
    return new Map(words.filter((word) => word.id).map((word) => [word.id!, word]));
};

const _buildAnswer = (
    answer: StudentAnswerReviewItem,
    exerciseId: string,
    index: number,
    maxScore: number,
    word: TestDetailsWord | undefined
): StudentResultAnswer => {
    const studentAnswer = answer.studentAnswer || "";
    const state = _getAnswerState(answer.teacherGrade, studentAnswer);

    return {
        // A render key only — the scoring matches words on the raw answer's id, in the caller.
        id: answer.id || `${exerciseId}_${index}`,
        question: answer.question || "",
        gapSentence: answer.gap_sentence || "",
        otherSide: answer.answer || "",
        studentAnswer,
        state,
        reachedScore: _getReachedScore(answer.teacherGrade, maxScore),
        maxScore,
        // The individual one wins: it is about this student's own answer.
        teacherComment: answer.teacherIndComment || answer.teacherComment || "",
        color: answer.color || "",
        isQuestionShown: word?.isQuestionShown ?? !!answer.question,
    };
};

/**
 * The result stores the pool shuffled at test start under a different name per exercise type, so
 * read it through here rather than picking one. `fillWords` is the test's own unshuffled order, so
 * it stands in only for an exercise the result never recorded.
 *
 * Connect-words is excluded although it fills both `connectWordsAnswers` and `fillWords`: there the
 * pool *is* the answer — the cards the student matched against — so the design draws it with the
 * answers rather than as a box above them.
 */
export const getWordBox = (
    questionAnswer: StudentQuestionAnswer | undefined,
    studentTestExercise: StudentTestExercise | undefined
): string[] | undefined => {
    const questionMode = questionAnswer?.exerciseType || studentTestExercise?.questionMode;
    if (questionMode === ExerciseTypes.get(Exercises.CONNECT_WORDS)) {
        return undefined;
    }

    return questionAnswer?.fillInTheGapWords || questionAnswer?.jumbledWords || studentTestExercise?.fillWords;
};

/**
 * The maximum comes from the student-test rather than from the answers, so an exercise the student
 * never opened still costs its points. Without the student-test it counts 0 — a made-up denominator
 * would read as authoritative and be wrong.
 */
const _buildResultExercise = (
    exerciseId: string,
    exerciseIndex: number,
    questionAnswer: StudentQuestionAnswer | undefined,
    studentTestExercise: StudentTestExercise | undefined,
    isScored: boolean
): StudentResultExercise => {
    const number = exerciseIndex + 1;
    const studentAnswers = questionAnswer?.studentAnswers || [];
    // The result's own exerciseType survives the student-test failing to load.
    const questionMode = questionAnswer?.exerciseType || studentTestExercise?.questionMode || "";

    const scoredExercise = studentTestExercise ? _getScoredExercise(studentTestExercise, isScored) : undefined;
    const exerciseMaxScore = scoredExercise ? getScoreForExercise(scoredExercise) : 0;
    const scoring = getExerciseScoringByMode(questionMode);
    const words: TestDetailsWord[] = scoredExercise?.selectedWords ?? [];
    const wordsById = _buildWordsById(words);

    const answers = studentAnswers.map((answer, answerIndex) => {
        const word = answer.id ? wordsById.get(answer.id) : undefined;
        // Every word is scored by now, so this only falls back for an answer matching no word at
        // all — a card saved without an id, which then scores 0 against a max that still counts it.
        const answerMaxScore = scoring.storesScorePerWord ? word?.wordScore ?? 0 : exerciseMaxScore;
        return _buildAnswer(answer, exerciseId, answerIndex, answerMaxScore, word);
    });

    const reachedScore = _sum(answers.map((answer) => answer.reachedScore));

    return {
        id: exerciseId,
        number,
        exerciseKind: getExerciseTypeByQuestionMode(questionMode) || undefined,
        description: studentTestExercise?.extraComments || "",
        wordBox: getWordBox(questionAnswer, studentTestExercise),
        helperSentence: questionAnswer?.textFieldStarterText || studentTestExercise?.textFieldStarterText,
        answers,
        reachedScore,
        maxScore: exerciseMaxScore,
        isScored,
    };
};

/**
 * The whole result. Exercises come from the student-test, plus any the result carries that the test
 * no longer lists — so neither a half-finished submission nor a rewritten test can hide answers.
 */
export const buildStudentResult = (resultData: StudentTestAnswer, studentTest: StudentTestContent): StudentResult => {
    const questionAnswers = resultData.questionAnswers || {};
    // Absent means unscored: the field is optional and defaults to off.
    const isScored = studentTest.showScores === true;

    // `removeAnswersFromTest` gives every shared exercise a positional "e1", "e2", … so these ids
    // match the result's keys. Each id is paired with its own exercise rather than looked up by
    // position later, so a missing id cannot pull in the neighbouring exercise's words.
    const testExercises: [string, StudentTestExercise | undefined][] = (studentTest.content || []).map(
        (studentTestExercise, index) => [studentTestExercise.id || `e${index + 1}`, studentTestExercise]
    );
    const testExerciseIds = testExercises.map(([exerciseId]) => exerciseId);

    const orphans: [string, StudentTestExercise | undefined][] = Object.keys(questionAnswers)
        .filter((exerciseId) => !testExerciseIds.includes(exerciseId))
        .map((exerciseId) => [exerciseId, undefined]);

    const exercises = [...testExercises, ...orphans].map(([exerciseId, studentTestExercise], index) =>
        _buildResultExercise(exerciseId, index, questionAnswers[exerciseId], studentTestExercise, isScored)
    );

    const reachedScore = _sum(exercises.map((exercise) => exercise.reachedScore));
    const maxScore = _sum(exercises.map((exercise) => exercise.maxScore));

    return {
        testName: studentTest.name || "",
        submittedDate: resultData.sentTime ?? 0,
        languageName: getDisplayableTestLanguageName(studentTest),
        exercises,
        reachedScore,
        maxScore,
        overallComment: resultData.teacherComment || "",
        isScored,
    };
};
